import React, { useEffect, useImperativeHandle, useRef } from 'react';

import { useReviewsSelection } from './context';
import { ReviewList, ReviewListItemProps, ReviewsSelectionProvider } from './review-list';
import { KnowledgeReviewPanel } from './knowledge-review-panel';

import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

import './knowledge-review.css';

const SCROLL_HEIGHT_VAR = '--knowledge-review-scroll-height';

export type KnowledgeReviewSceneHandle = {
  selectedItem: ReviewListItemProps | null;
  setSelectedItem: (item: ReviewListItemProps | null) => void;
};

export type KnowledgeReviewSceneProps = {
  diffTitle?: string;
  children: React.ReactNode;
  items: ReviewListItemProps[];
  isLoading?: boolean;
  /** Total number of review list items available (can exceed currently loaded `items.length`). */
  totalItemsCount?: number;
  /** Controls visibility of the Reject / Accept action buttons in the review header. */
  showActionButtons?: boolean;
  className?: string;
  onApproveAllSuggestions: () => void;
  onRejectAllSuggestions: () => void;
  onAcceptAndPublish?: () => void;
  onOpenArticleLink?: () => void;
  onSourceLinkClick?: () => void;
  breadcrumb?: string;
  sourceIcon: React.ReactNode;
  sourceDescription: string | null;
  suggestionReasonBody?: string;
};

const KnowledgeReviewSceneInner = React.forwardRef<KnowledgeReviewSceneHandle, KnowledgeReviewSceneProps>(
  function KnowledgeReviewSceneInner(
    {
      diffTitle = 'Knowledge Review',
      children,
      items,
      isLoading,
      totalItemsCount,
      showActionButtons = true,
      className,
      onApproveAllSuggestions,
      onRejectAllSuggestions,
      onOpenArticleLink,
      onAcceptAndPublish,
      onSourceLinkClick,
      breadcrumb,
      sourceIcon,
      sourceDescription,
      suggestionReasonBody,
      ...props
    },
    ref,
  ) {
    const innerRef = useRef<HTMLDivElement>(null);
    const { selectedItem, setSelectedItem } = useReviewsSelection();

    useImperativeHandle(
      ref,
      () => ({
        selectedItem,
        setSelectedItem,
      }),
      [selectedItem, setSelectedItem],
    );

    useEffect(() => {
      const cardEl = innerRef.current;
      if (!cardEl) return;

      const updateHeight = () => {
        const { height } = cardEl.getBoundingClientRect();
        cardEl.style.setProperty(SCROLL_HEIGHT_VAR, `${height}px`);
      };

      updateHeight();

      if (typeof ResizeObserver !== 'function') {
        return;
      }

      const observer = new ResizeObserver(updateHeight);
      observer.observe(cardEl);

      return () => observer.disconnect();
    }, []);

    const resolvedIsLoading = isLoading ?? items.length === 0;
    const isLoadingItems = resolvedIsLoading && items.length === 0;

    return (
      <Card className={cn('h-full', className)}>
        <CardContent className="p-4 lg:p-8 h-full">
          <div
            ref={innerRef}
            className="grid xs:max-lg:grid-cols-1 lg:grid-cols-[1fr_4fr] gap-6 min-h-96 h-full"
            style={{ [SCROLL_HEIGHT_VAR]: '100px' } as React.CSSProperties}
            {...props}
          >
            <ScrollArea className="h-[calc(var(--knowledge-review-scroll-height))] min-w-80 lg:block hidden">
              {isLoadingItems ? (
                <div className="flex h-full min-h-96 items-center justify-center text-muted-foreground">
                  <Spinner className="size-6" />
                </div>
              ) : (
                <ReviewList items={items} />
              )}
            </ScrollArea>
            <KnowledgeReviewPanel
              diffTitle={diffTitle}
              items={items}
              isLoading={resolvedIsLoading}
              totalItemsCount={totalItemsCount}
              showActionButtons={showActionButtons}
              sourceIcon={sourceIcon}
              sourceDescription={sourceDescription}
            >
              {children}
            </KnowledgeReviewPanel>
          </div>
        </CardContent>
      </Card>
    );
  },
);

export const KnowledgeReviewScene = React.forwardRef<KnowledgeReviewSceneHandle, KnowledgeReviewSceneProps>(
  function KnowledgeReviewScene(props, ref) {
    const {
      items,
      onApproveAllSuggestions,
      onRejectAllSuggestions,
      onAcceptAndPublish,
      onOpenArticleLink,
      onSourceLinkClick,
      breadcrumb,
      suggestionReasonBody,
    } = props;

    return (
      <ReviewsSelectionProvider
        defaultSelectedItem={items.find((m) => m.isSelected) ?? null}
        onApproveAllSuggestions={onApproveAllSuggestions}
        onRejectAllSuggestions={onRejectAllSuggestions}
        onAcceptAndPublish={onAcceptAndPublish}
        onOpenArticleLink={onOpenArticleLink}
        onSourceLinkClick={onSourceLinkClick}
        breadcrumb={breadcrumb}
        suggestionReasonBody={suggestionReasonBody}
      >
        <KnowledgeReviewSceneInner {...props} ref={ref} />
      </ReviewsSelectionProvider>
    );
  },
);
